import { sortBy } from 'lodash';
import React, {
    createContext,
    type ComponentPropsWithoutRef,
    useState,
    useRef,
    type ElementType,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    type FocusEventHandler,
} from 'react';

export type KeyboardNavigationItem = HTMLElement;

type KeyboardNavigationContextProps = {
    onKeyDownHandler: (
        e: React.KeyboardEvent<HTMLElement>,
        hasRemoveButton: boolean
    ) => void;
};

export const KeyboardNavigationContext =
    createContext<KeyboardNavigationContextProps>({
        onKeyDownHandler: () => {},
    });

const focusableElements = [
    'button:not([disabled])',
    '[tabindex]:not([disabled]):not([hidden]):not(svg)',
];

export const getNextFocusableItem = (
    orderedItems: KeyboardNavigationItem[],
    currIndex: number
): KeyboardNavigationItem | undefined => {
    return orderedItems.at(
        currIndex === orderedItems.length - 1 ? 0 : currIndex + 1
    );
};

export const getNextSameTypeFocusableItem = (
    orderedItems: KeyboardNavigationItem[],
    currIndex: number
): KeyboardNavigationItem | undefined => {
    if (currIndex === orderedItems.length - 2) {
        return orderedItems.at(0);
    }

    if (currIndex === orderedItems.length - 1) {
        return orderedItems.at(1);
    }

    return orderedItems.at(currIndex + 2);
};

export const getPrevFocusableItem = (
    orderedItems: KeyboardNavigationItem[],
    currIndex: number
): KeyboardNavigationItem | undefined => {
    return orderedItems.at(currIndex === 0 ? -1 : currIndex - 1);
};

export const getPrevSameTypeFocusableItem = (
    orderedItems: KeyboardNavigationItem[],
    currIndex: number
): KeyboardNavigationItem | undefined => {
    return orderedItems.at(currIndex === 0 ? -2 : currIndex - 2);
};

export const getActiveIndex = (
    event: React.SyntheticEvent<HTMLElement>,
    items: KeyboardNavigationItem[],
    container: HTMLElement | null
) => {
    const currentElement = event?.target || container?.querySelector(':focus');
    return items.indexOf(currentElement as HTMLElement);
};

type KeyboardNavigationRootBaseProps<T extends ElementType> = {
    children: ReactNode | ReactNode[];
    as?: T;
};

type KeyboardNavigationRootProps<T extends ElementType> =
    KeyboardNavigationRootBaseProps<T> &
        Omit<
            ComponentPropsWithoutRef<T>,
            keyof KeyboardNavigationRootBaseProps<T>
        >;

export const KeyboardNavigationRoot = <T extends ElementType>({
    children,
    as,
    ...props
}: KeyboardNavigationRootProps<T>) => {
    const Component = as ?? 'div';
    const [isTabbing, setIsTabbing] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);
    const getOrderedItems = useCallback(() => {
        if (!ref.current) {
            return [];
        }
        const elementsFromDOM = Array.from(
            ref.current.querySelectorAll<HTMLElement>(
                focusableElements.join(', ')
            )
        );

        return sortBy(elementsFromDOM, (el) => {
            if (el.tabIndex > 0) {
                return -1 / el.tabIndex;
            }
            return 0;
        });
    }, []);

    useEffect(() => {
        if (isTabbing) {
            setIsTabbing(false);
        }
    }, [isTabbing]);

    const handleTabKey = useCallback(() => {
        setIsTabbing(true);
    }, [setIsTabbing]);

    const handleFocus = useCallback<FocusEventHandler>(
        (e) => {
            if (e.target !== e.currentTarget || isTabbing) {
                return;
            }

            const orderedItems = getOrderedItems();

            if (orderedItems.length === 0) {
                return;
            }

            orderedItems[0].focus();
        },
        [getOrderedItems, isTabbing]
    );

    const onKeyDownHandler = useCallback(
        (e: React.KeyboardEvent<HTMLElement>, hasRemoveButton: boolean) => {
            const galleryContainer = document.querySelector(
                '[data-test="gallery-list"]'
            );

            // this is needed to prevent closing dropdown when tabbing from gallery list to gallery upload
            if (galleryContainer?.nextElementSibling) {
                e.stopPropagation();
            }

            const items = getOrderedItems();
            let nextItem: KeyboardNavigationItem | undefined;

            const activeIndex = getActiveIndex(e, items, ref.current);

            if (e.key === 'ArrowRight') {
                e.preventDefault();
                nextItem = getNextFocusableItem(items, activeIndex);
                nextItem?.focus();
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                nextItem = getPrevFocusableItem(items, activeIndex);
                nextItem?.focus();
            }

            if (hasRemoveButton) {
                if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    nextItem = getPrevSameTypeFocusableItem(items, activeIndex);
                    nextItem?.focus();
                } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    nextItem = getNextSameTypeFocusableItem(items, activeIndex);
                    nextItem?.focus();
                }
            }

            if (e.key === 'Tab') {
                handleTabKey();
            }
        },
        [getOrderedItems, handleTabKey]
    );

    const providerValue = useMemo(
        () => ({
            onKeyDownHandler,
        }),
        [onKeyDownHandler]
    );

    return (
        <KeyboardNavigationContext.Provider value={providerValue}>
            <Component
                {...props}
                ref={ref}
                tabIndex={isTabbing ? -1 : 0}
                onFocus={handleFocus}
            >
                {children}
            </Component>
        </KeyboardNavigationContext.Provider>
    );
};
